// Shared pixel-scene engine for the theme skins.
//
// Every skin's PromoteHero scene is a low-res canvas scaled up nearest-neighbour.
// The machinery is identical across skins - canvas setup, the rAF loop, reading
// live CSS tokens, repaint-only-on-change, reduced-motion, and the traveler's
// position math - so it lives here. A skin supplies just two functions:
//   colors(g)  -> its palette object, read from CSS tokens via `g('--token')`
//   draw(C, s) -> paint one frame using the SceneCtx primitives
// `makeScene({ colors, draw })` returns the ready <canvas> component.
import { useEffect, useRef, type FC } from 'react';

// The four states the promote hero can be in. Bound to `latestPromotion.state`
// plus a transient "just shipped" flash - see PromoteHero.
export type HeroState = 'idle' | 'pending' | 'failure' | 'success';

export type PromoteSceneProps = {
  state: HeroState;
  /** 0..1 cosmetic progress (the pipeline reports no real %); drives hero position. */
  progress: number;
};

// Scene geometry (logical px of the canvas buffer). One source of truth so the
// speech bubble can track the traveler and every skin shares the same short buffer.
export const GEO = { w: 220, h: 47, groundY: 37, pathL: 8, pathRInset: 46 } as const;

/** Traveler left-x (logical px) for a state/progress - rest near the goal on idle,
 *  at the goal on success, else walking the path. Shared by all skins. */
export function heroX(state: HeroState, progress: number, w: number = GEO.w): number {
  const pathR = w - GEO.pathRInset;
  if (state === 'idle') return w - 52;
  if (state === 'success') return w - 34;
  return GEO.pathL + progress * (pathR - GEO.pathL);
}

/** Horizontal centre of the traveler as a % of scene width, for the speech bubble. */
export function heroCenterPct(state: HeroState, progress: number): number {
  return Math.max(16, Math.min(92, ((heroX(state, progress) + 6) / GEO.w) * 100));
}

// ---- colour helpers (shared) ----
export function hex(s: string): [number, number, number] {
  if (s[0] === '#') return [parseInt(s.slice(1, 3), 16), parseInt(s.slice(3, 5), 16), parseInt(s.slice(5, 7), 16)];
  const m = s.match(/\d+/g);
  return m ? [+m[0], +m[1], +m[2]] : [0, 0, 0];
}
export function mix(a: string, b: string, t: number): string {
  const pa = hex(a), pb = hex(b);
  const c = (i: number) => Math.round(pa[i] + (pb[i] - pa[i]) * t);
  return `rgb(${c(0)},${c(1)},${c(2)})`;
}

export type Palette = Record<string, string>;

/** Primitives + frame data handed to a skin's draw() each painted frame. */
export type SceneCtx = {
  state: HeroState;
  progress: number;
  heroX: number;      // traveler left-x (logical px)
  walkF: number;      // walk-cycle frame (0 under reduced motion)
  flameF: number;     // 0/1 flicker frame (0 under reduced motion)
  W: number; H: number;
  groundY: number; pathL: number; pathR: number;
  px: (x: number, y: number, w: number, h: number, c: string) => void;
  glow: (x: number, y: number, w: number, h: number, c: string, alpha: number) => void;
  mix: typeof mix;
};

export type SceneModule = {
  /** Build the palette from a CSS-token getter (so scenes follow theme + skin). */
  colors: (g: (name: string) => string) => Palette;
  /** Paint one frame. Draw back-to-front: sky -> ground -> path -> props -> traveler -> obstacle. */
  draw: (C: Palette, s: SceneCtx) => void;
};

export function makeScene({ colors, draw }: SceneModule): FC<PromoteSceneProps> {
  return function PixelScene({ state, progress }: PromoteSceneProps) {
    const ref = useRef<HTMLCanvasElement>(null);
    // Read latest props inside the rAF loop without restarting it every render.
    const stateRef = useRef(state);
    const progRef = useRef(progress);
    stateRef.current = state;
    progRef.current = progress;

    useEffect(() => {
      const cv = ref.current;
      const ctx0 = cv?.getContext('2d');
      if (!cv || !ctx0) return;
      const ctx = ctx0; // non-null const so the closures keep the narrowing
      ctx.imageSmoothingEnabled = false;
      const W = cv.width, H = cv.height, root = document.documentElement;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const groundY = GEO.groundY, pathL = GEO.pathL, pathR = W - GEO.pathRInset;

      let raf = 0, t = 0, themeKey = '', lastKey = '';
      let C: Palette = {};
      const px = (x: number, y: number, w: number, h: number, c: string) => { ctx.fillStyle = c; ctx.fillRect(x | 0, y | 0, w | 0, h | 0); };
      const glow = (x: number, y: number, w: number, h: number, c: string, a: number) => {
        ctx.fillStyle = c; ctx.globalAlpha = a; ctx.fillRect(x | 0, y | 0, w | 0, h | 0); ctx.globalAlpha = 1;
      };
      const readColors = () => { const cs = getComputedStyle(root); C = colors((n) => cs.getPropertyValue(n).trim()); };

      function frame() {
        t++;
        const key = `${root.dataset.theme || ''}|${root.dataset.skin || ''}`;
        if (key !== themeKey) { themeKey = key; readColors(); }
        const st = stateRef.current, p = progRef.current;
        const walkF = reduce ? 0 : Math.floor(t / 9);
        const flameF = reduce ? 0 : Math.floor(t / 8) % 2;
        // Repaint only when something visible changed (also -> a static reduced-motion frame).
        const dirty = `${st}|${Math.round(p * 100)}|${themeKey}|${walkF}|${flameF}`;
        if (dirty !== lastKey) {
          lastKey = dirty;
          ctx.clearRect(0, 0, W, H);
          draw(C, {
            state: st, progress: p, heroX: heroX(st, p, W), walkF, flameF,
            W, H, groundY, pathL, pathR, px, glow, mix,
          });
        }
        raf = requestAnimationFrame(frame);
      }
      raf = requestAnimationFrame(frame);
      return () => cancelAnimationFrame(raf);
    }, []);

    return <canvas ref={ref} className="hero-canvas" width={GEO.w} height={GEO.h} aria-hidden />;
  };
}
